import { DocumentClassKey } from '@ps-gui/claims-terminal-shared';
import { associateWithNotNull, keys } from '@ps-gui/shared-utils';

/**
 * Build simple omits the key of every field it did not extract. Hand-building a members object
 * from named keys therefore produces keys whose value is `undefined`, which
 * `DocumentGroupItemMembers` does not admit and no consumer down the line guards against.
 */
const definedMembers = (
  members: Record<string, ExtractedEntityValue | undefined>,
): DocumentGroupItemMembers => associateWithNotNull(keys(members), (key) => members[key]);

const map301data = (data: DocumentData | undefined): DocumentData | undefined => {
  const entities: DocumentEntities = {
    '301er_daten_ik_nummer': data?.entities['institution_ikNummer'] ?? [],
    '301er_daten_vorname_geschaedigter': data?.entities['geschaedigter_vornameGeschaedigter'] ?? [],
    '301er_daten_nachname_geschaedigter':
      data?.entities['geschaedigter_nachnameGeschaedigter'] ?? [],
    '301er_daten_geschlecht': data?.entities['geschaedigter_geschlecht'] ?? [],
    '301er_daten_geburtsdatum_geschaedigter': data?.entities['geschaedigter_geburtsdatum'] ?? [],
    '301er_daten_aufnahmedatum': data?.entities['aufnahmeEntlassung_aufnahmedatum'] ?? [],
    '301er_daten_aufnahmeuhrzeit': data?.entities['aufnahmeEntlassung_aufnahmezeit'] ?? [],
    '301er_daten_entlassungsdatum': data?.entities['aufnahmeEntlassung_entlassungsdatum'] ?? [],
    '301er_daten_entlassungsuhrzeit': data?.entities['aufnahmeEntlassung_entlassungszeit'] ?? [],
    '301er_daten_beatmungsstunden': data?.entities['abrechnungsdaten_beatmungsstunden'] ?? [],
    '301er_daten_tage_ohne_brechung':
      data?.entities['abrechnungsdaten_tageOhneBerechnungTob'] ?? [],
    '301er_daten_drg': data?.entities['abrechnungsdaten_DRG'] ?? [],
  };
  const groups: DocumentGroups = {
    diagnosen:
      data?.groups['diagnosen']?.map((diagnosenRow) => {
        return {
          members: definedMembers({
            diagnosen_icd10_code: diagnosenRow.members['diagnosen_icd10Code'],
            diagnosen_lokalisation: diagnosenRow.members['diagnosen_lokalisation'],
          }),
          verified: diagnosenRow.verified,
        };
      }) ?? [],
    operationen_und_prozeduren:
      data?.groups['operationenUndProzeduren']?.map((operationenUndProzedurenRow) => {
        return {
          members: definedMembers({
            operationen_und_prozeduren_ops_code:
              operationenUndProzedurenRow.members['operationenUndProzeduren_opsCode'],
            operationen_und_prozeduren_lokalisation:
              operationenUndProzedurenRow.members['operationenUndProzeduren_lokalisation'],
            operationen_und_prozeduren_leistungsdatum:
              operationenUndProzedurenRow.members['operationenUndProzeduren_leistungsdatum'],
          }),
          verified: operationenUndProzedurenRow.verified,
        };
      }) ?? [],
  };
  return { ...data, entities, groups };
};

const mapperDict: Record<string, (data: DocumentData | undefined) => DocumentData | undefined> = {
  '25_Q3_301ER_DATEN': map301data,
};

export const mapExtractedData = (data: DocumentExtractionResults): DocumentExtractionResults => {
  keys(data).forEach((documentId) => {
    keys(data[documentId]).forEach((documentClass) => {
      const documentData = data[documentId][documentClass];
      const mapper = mapperDict[documentClass];
      data[documentId][documentClass] = mapper ? mapper(documentData) : documentData;
    });
  });
  return data;
};

export type DocumentExtractionResults = Record<
  string,
  Record<DocumentClassKey, DocumentData | undefined>
>;

type DocumentData = {
  entities: DocumentEntities;
  groups: DocumentGroups;
};

type DocumentEntities = Record<string, ExtractedEntityValue[]>;

type DocumentGroups = Record<string, DocumentGroupItem[]>;

type DocumentGroupItem = {
  members: DocumentGroupItemMembers;
  verified: boolean;
};

type DocumentGroupItemMembers = Record<string, ExtractedEntityValue>;

export type ExtractedEntityValue = {
  value: string;
  originalValue: string;
  verified: boolean | null;
  dataType: 'STRING' | 'DATE' | string;
  isValid: boolean;
  validationErrors: unknown[];
  normalizedValue: string;
  location: ExtractedLocation;
  confidence: number;
};

type ExtractedLocation = {
  page: number;
  left: number;
  right: number;
  upper: number;
  lower: number;
};
